import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ManageRentals.css';
import './WaterUsageView.css';
import WaterUsagePanel from './WaterUsagePanel';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import { getMyProperties, getPropertyUnits } from '../../../api/services/propertyService';
import { getMyAgentAssignments } from '../../../api/services/agentAssignmentsService';

const HISTORY_KEY = 'tenantscore_waterUsageDetail';

function unitsFromApiResponse(res) {
  if (!res || res.status !== 'success' || !res.data) return [];
  const d = res.data;
  if (Array.isArray(d)) return d;
  if (d.units && Array.isArray(d.units)) return d.units;
  if (d.data && Array.isArray(d.data)) return d.data;
  return [];
}

function propertiesFromAgentAssignmentsResponse(res) {
  const d = res?.data;
  const list = Array.isArray(d?.content) ? d.content : Array.isArray(d) ? d : [];
  return list
    .filter((a) => a && a.propertyId)
    .map((a) => ({
      id: a.propertyId,
      name: a.propertyName || '',
      address: a.propertyAddress || '',
      location: a.location || undefined,
    }));
}

const WaterUsageView = ({ isAgentView = false }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPropertyId, setSelectedPropertyId] = useState(null);
  const [selectedPropertyName, setSelectedPropertyName] = useState('');
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const selectedPropertyIdRef = useRef(null);
  selectedPropertyIdRef.current = selectedPropertyId;

  const filteredProperties = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return properties;
    return properties.filter((p) => {
      const nameMatch = p.name?.toLowerCase().includes(q);
      const addressMatch = p.address?.toLowerCase().includes(q);
      return nameMatch || addressMatch;
    });
  }, [properties, searchQuery]);

  useEffect(() => {
    if (isAgentView) return undefined;
    let cancelled = false;
    const fetchLandlordProperties = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyProperties({ page: 0, size: 100, search: searchQuery });
        if (cancelled) return;
        if (response.status === 'success' && response.data?.content) {
          setProperties(response.data.content);
        } else {
          setProperties([]);
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(e.message || 'Failed to load properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchLandlordProperties();
    return () => {
      cancelled = true;
    };
  }, [isAgentView, searchQuery]);

  useEffect(() => {
    if (!isAgentView) return undefined;
    let cancelled = false;
    const fetchAgentAssignments = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getMyAgentAssignments({ page: 0, size: 100 });
        if (cancelled) return;
        setProperties(propertiesFromAgentAssignmentsResponse(response));
      } catch (e) {
        if (!cancelled) {
          setErrorMessage(e.message || 'Failed to load assigned properties.');
          setProperties([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAgentAssignments();
    return () => {
      cancelled = true;
    };
  }, [isAgentView]);

  useEffect(() => {
    if (!selectedPropertyId) {
      setUnits([]);
      return undefined;
    }
    let cancelled = false;
    const loadUnits = async () => {
      setUnitsLoading(true);
      try {
        const fromList = properties.find((p) => p.id === selectedPropertyId);
        const embedded = fromList?.units;
        if (Array.isArray(embedded) && embedded.length > 0) {
          if (!cancelled) setUnits(embedded);
          return;
        }
        const res = await getPropertyUnits(selectedPropertyId);
        if (!cancelled) setUnits(unitsFromApiResponse(res));
      } catch (_) {
        if (!cancelled) setUnits([]);
      } finally {
        if (!cancelled) setUnitsLoading(false);
      }
    };
    loadUnits();
    return () => {
      cancelled = true;
    };
  }, [selectedPropertyId, properties]);

  useEffect(() => {
    const onPopState = () => {
      if (selectedPropertyIdRef.current != null) {
        setSelectedPropertyId(null);
        setSelectedPropertyName('');
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const openProperty = (property) => {
    window.history.pushState({ [HISTORY_KEY]: true, propertyId: property.id }, '', window.location.href);
    setSelectedPropertyId(property.id);
    setSelectedPropertyName(property.name || '');
  };

  if (selectedPropertyId) {
    return (
      <div className="manage-rentals-container water-usage-view">
        <CollapsibleTopPanel id="water-usage-detail-top-panel" defaultOpenOnMobile={false}>
          <div className="water-usage-view__detail-header page-header">
            <div className="water-usage-view__title-wrap">
              <h1 className="page-title water-usage-view__title">Water usage</h1>
              {selectedPropertyName ? (
                <p className="water-usage-view__property-name">{selectedPropertyName}</p>
              ) : null}
            </div>
          </div>
        </CollapsibleTopPanel>
        {unitsLoading ? (
          <div className="empty-state">Loading property units...</div>
        ) : (
          <WaterUsagePanel propertyId={selectedPropertyId} units={units} />
        )}
      </div>
    );
  }

  return (
    <div className="manage-rentals-container water-usage-view">
      <CollapsibleTopPanel id="water-usage-top-panel" defaultOpenOnMobile={false}>
        <div className="page-header">
          <h1 className="page-title">Water usage</h1>
          <div className="page-header-actions">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </CollapsibleTopPanel>

      {errorMessage ? (
        <div className="water-usage-alert water-usage-alert--error">{errorMessage}</div>
      ) : null}

      {loading ? (
        <div className="empty-state">Loading properties...</div>
      ) : filteredProperties.length === 0 ? (
        <div className="empty-state">
          {isAgentView ? 'No properties assigned to you yet.' : 'No properties available.'}
        </div>
      ) : (
        <div className="properties-grid-overview">
          {filteredProperties.map((property) => {
            const addrLine = property.address?.trim() || '';
            const cityLine = property.location?.city?.trim() || '';
            const primaryLocationLine = addrLine || cityLine;

            return (
              <button
                key={property.id}
                type="button"
                className="property-overview-card water-usage-view__card"
                onClick={() => openProperty(property)}
              >
                <div className="property-overview-header">
                  <h3>{property.name}</h3>
                  {primaryLocationLine ? (
                    <p className="property-overview-address">{primaryLocationLine}</p>
                  ) : null}
                </div>
                <span className="water-usage-view__card-cta">Open water usage form →</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WaterUsageView;
